import React, { useState, useEffect } from "react";
import {
  ShieldCheck,
  FileText,
  Scale,
  AlertTriangle,
  CheckCircle2,
  Lock,
  GraduationCap,
  Copyright,
  ArrowLeft,
  Printer,
  Share2,
  ChevronRight,
  ExternalLink,
  BookOpen,
  Info,
  Building,
  Check,
  HelpCircle,
  Mail
} from "lucide-react";
import { shareWebsite, getWebsiteAddress } from "../lib/shareUtils";
import { FORENSIC_SCORE_TIERS } from "../lib/scoreBands";
import { CatchAiLogo } from "./CatchAiLogo";

export type LegalSectionId = "terms" | "privacy" | "ethics" | "about" | "dmca" | "score-guide";

interface LegalHubPageProps {
  initialSection?: LegalSectionId;
  onNavigateHome: () => void;
  onNavigateSection?: (section: LegalSectionId) => void;
}

export const LegalHubPage: React.FC<LegalHubPageProps> = ({
  initialSection = "privacy",
  onNavigateHome,
  onNavigateSection
}) => {
  const [activeSection, setActiveSection] = useState<LegalSectionId>(initialSection);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  const handleSelectSection = (section: LegalSectionId) => {
    setActiveSection(section);
    if (onNavigateSection) {
      onNavigateSection(section);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCopyPageLink = async () => {
    try {
      const res = await shareWebsite({
        title: `CatchAI Text — ${activeSection === "terms" ? "Terms of Service" : activeSection === "privacy" ? "Privacy Policy" : activeSection === "ethics" ? "AI Ethics" : activeSection === "score-guide" ? "Score Guide" : "Documentation"}`,
        text: "CatchAI Text — Transparent stylometric authorship forensics & provenance verification platform.",
        subpath: `/${activeSection}`
      });
      if (res.shared || res.copied) {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
    } catch {}
  };

  const navItems: { id: LegalSectionId; title: string; subtitle: string; icon: React.ReactNode }[] = [
    {
      id: "terms",
      title: "Terms of Service",
      subtitle: "Advisory rules & human-in-the-loop requirement",
      icon: <FileText className="w-4 h-4" />
    },
    {
      id: "privacy",
      title: "Privacy Policy",
      subtitle: "Zero document storage, CIPA & self-hosted fonts",
      icon: <Lock className="w-4 h-4" />
    },
    {
      id: "ethics",
      title: "AI Ethics & Governance",
      subtitle: "EU AI Act, non-native ESL equity & bias safeguards",
      icon: <Scale className="w-4 h-4" />
    },
    {
      id: "score-guide",
      title: "How to Interpret Scores",
      subtitle: "Probability bands, false-positive safety & guidelines",
      icon: <HelpCircle className="w-4 h-4" />
    },
    {
      id: "about",
      title: "About CatchAI Text",
      subtitle: "Scientific methodology & stylometric principles",
      icon: <Building className="w-4 h-4" />
    },
    {
      id: "dmca",
      title: "DMCA & Copyright",
      subtitle: "17 U.S.C. § 512 Safe Harbor & designated agent",
      icon: <Copyright className="w-4 h-4" />
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans transition-colors duration-200 antialiased selection:bg-slate-900 selection:text-white dark:selection:bg-white dark:selection:text-slate-900">
      
      {/* Top Standard Navigation Bar (Like Claude / Stripe Legal Docs) */}
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          
          <div className="flex items-center gap-4">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                onNavigateHome();
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-200 transition-colors cursor-pointer"
              title="Return to CatchAI Text Detector"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Detector</span>
            </a>

            <div className="h-4 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />

            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                onNavigateHome();
              }}
              className="flex items-center gap-2.5 cursor-pointer select-none group"
              title="CatchAI Text Home"
            >
              <CatchAiLogo size={28} className="shrink-0" />
              <span className="font-display font-bold text-base text-slate-900 dark:text-white tracking-tight">
                CatchAI <span className="text-indigo-600 dark:text-indigo-400 font-extrabold">Text</span> <span className="font-normal text-slate-400 text-sm ml-1">Legal &amp; Policies</span>
              </span>
            </a>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors cursor-pointer hidden sm:inline-flex"
              title="Print document"
              aria-label="Print document"
            >
              <Printer className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={handleCopyPageLink}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 transition-colors cursor-pointer"
              title={`Share website address (${getWebsiteAddress(`/${activeSection}`)})`}
              id="legal-hub-share-btn"
            >
              {copiedLink ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Link Copied!</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Two-Column Layout (Sidebar Navigation + Editorial Content Area) */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-12 flex-1 flex flex-col lg:flex-row gap-6 lg:gap-12">
        
        {/* Mobile Horizontal Navigation Strip (visible < lg) */}
        <div className="flex lg:hidden overflow-x-auto touch-scroll-x gap-1.5 pb-2 -mx-4 px-4 border-b border-slate-200 dark:border-slate-800" role="tablist" aria-label="Legal Sections">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <a
                key={item.id}
                href={`/${item.id}`}
                role="tab"
                aria-selected={isActive}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectSection(item.id);
                }}
                className={`py-2 px-3 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 shrink-0 min-h-[38px] ${
                  isActive
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950 shadow-xs"
                    : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100"
                }`}
              >
                <span className={isActive ? "text-white dark:text-slate-950" : "text-slate-400"}>
                  {item.icon}
                </span>
                <span>{item.title}</span>
              </a>
            );
          })}
        </div>

        {/* Left Sticky Sidebar for Laptop & Desktop (visible >= lg) */}
        <aside className="hidden lg:block w-72 shrink-0">
          <div className="lg:sticky lg:top-24 space-y-4">
            
            <div className="px-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Documentation &amp; Terms
              </span>
            </div>

            <nav aria-label="Legal Center Navigation" className="flex flex-col gap-1">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <a
                    key={item.id}
                    href={`/${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSelectSection(item.id);
                    }}
                    className={`w-full text-left p-3 rounded-xl transition-all cursor-pointer flex items-center justify-between gap-3 ${
                      isActive
                        ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950 shadow-sm font-semibold"
                        : "hover:bg-slate-100 dark:hover:bg-slate-850 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`p-1 rounded-md ${isActive ? "text-white dark:text-slate-950" : "text-slate-500 dark:text-slate-400"}`}>
                        {item.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs sm:text-sm font-bold truncate">
                          {item.title}
                        </div>
                        <div className={`text-[11px] truncate ${isActive ? "text-slate-300 dark:text-slate-600" : "text-slate-600 dark:text-slate-400"}`}>
                          {item.subtitle}
                        </div>
                      </div>
                    </div>

                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${isActive ? "opacity-100" : "opacity-30"}`} />
                  </a>
                );
              })}
            </nav>

            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-2 mt-6">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span>Zero Retention Guarantee</span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                Documents scanned in CatchAI Text are processed in volatile memory via WebAssembly and never stored, indexed, or shared.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-2 mt-4">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-900 dark:text-white">
                <Mail className="w-4 h-4 text-indigo-500" />
                <span>Support &amp; Inquiries</span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                Questions or technical support:
              </p>
              <a
                href="mailto:catchaitext@gmail.com"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline break-all"
              >
                catchaitext@gmail.com
              </a>
            </div>

          </div>
        </aside>

        {/* Right Editorial Content Center */}
        <main className="flex-1 bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-850 rounded-2xl sm:rounded-3xl p-6 sm:p-10 lg:p-12 shadow-xs min-w-0">
          
          {/* Breadcrumb & Metadata Header */}
          <div className="border-b border-slate-100 dark:border-slate-800 pb-6 mb-8">
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2">
              <span>CatchAI Text</span>
              <span>/</span>
              <span>Legal &amp; Policy Hub</span>
              <span>/</span>
              <span className="text-slate-900 dark:text-white font-medium capitalize">
                {activeSection}
              </span>
            </div>

            <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white tracking-tight">
              {activeSection === "terms" && "Terms of Service"}
              {activeSection === "privacy" && "Privacy Policy"}
              {activeSection === "ethics" && "AI Ethics & Algorithmic Governance"}
              {activeSection === "about" && "About CatchAI Text & Methodology"}
              {activeSection === "dmca" && "DMCA Safe Harbor & Copyright Policy"}
              {activeSection === "score-guide" && "How to Interpret Forensic Scores & Confidence Bands"}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mt-2">
              <span>Last updated: October 2026</span>
              <span>&bull;</span>
              <span>Effective: Academic Year 2026–2027</span>
              <span>&bull;</span>
              <span>Version 3.0</span>
            </div>
          </div>

          {/* 1. TERMS OF SERVICE */}
          {activeSection === "terms" && (
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 flex items-start gap-3.5 not-prose">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-amber-950 dark:text-amber-200 text-sm">
                    Diagnostic Aid Only — Mandatory Human Pedagogical Review
                  </h4>
                  <p className="text-xs text-amber-900/90 dark:text-amber-300/90 mt-0.5 leading-relaxed">
                    Stylometric analysis calculates statistical probabilities, lexical surprisal, and syntax variance. It does not provide deterministic proof of authorship. Academic misconduct determinations must never be automated.
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  1. Acceptance of Terms &amp; Educational Scope
                </h3>
                <p>
                  By accessing or using CatchAI Text, you agree to be bound by these Terms of Service. CatchAI Text provides transparent, open-access stylometric writing analysis and authorship verification.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  2. Advisory Nature of Authorship Predictions
                </h3>
                <p>
                  All detection percentages, heatmaps, and classification scores generated by CatchAI Text are probabilistic estimations derived from empirical statistical distributions. No automated linguistic model can achieve 100% accuracy. Borderline results (30%–70%) inherently reflect mixed rhetorical styles, formal academic jargon, or hybrid co-writing.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  3. Mandatory Human-in-the-Loop Protocol
                </h3>
                <p>
                  Instructors, integrity committees, editors, and students using CatchAI Text agree to conduct an independent human pedagogical review prior to initiating any formal academic proceedings. Appropriate verification protocols include examining progressive edit histories, checking outline drafts, and conducting formative student writing conferences.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  4. Limitation of Liability
                </h3>
                <p>
                  Under no circumstances shall the authors, developers, or affiliated academic bodies be liable for any academic sanctions, disciplinary proceedings, grade disputes, reputational harm, or lost grading resulting from reliance on algorithmic scores produced by this software.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  5. Age Verification &amp; Educational Eligibility
                </h3>
                <p>
                  By accessing CatchAI Text, you represent and warrant that you are at least 13 years of age, or an educational institution or student using the service under educator guidance. In accordance with COPPA and FERPA educational consent frameworks, children under 13 must operate with teacher or parental consent.
                </p>
              </section>
            </article>
          )}

          {/* 2. PRIVACY POLICY */}
          {activeSection === "privacy" && (
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 flex items-start gap-3.5 not-prose">
                <Lock className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-emerald-950 dark:text-emerald-200 text-sm">
                    Zero Cloud Retention &bull; Client-Side WebAssembly Architecture
                  </h4>
                  <p className="text-xs text-emerald-900/90 dark:text-emerald-300/90 mt-0.5 leading-relaxed">
                    Documents pasted or uploaded are analyzed in volatile memory on your device. Your work is never uploaded to remote databases, stored, or used for model training.
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  1. Zero Document Retention Policy
                </h3>
                <p>
                  CatchAI Text executes mathematical stylometry directly inside your browser using compiled WebAssembly routines. Zero bytes of your analyzed text are written to server hard drives or external storage. When your browser session terminates, in-memory tokens are completely wiped.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  2. No Training on User Documents
                </h3>
                <p>
                  We operate a strict non-commercial educational model. Your submissions are never used to train, fine-tune, or calibrate public or commercial machine learning models. Your intellectual property remains solely your own.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  3. Zero Session Replay &amp; CIPA Wiretapping Immunity
                </h3>
                <p>
                  In compliance with the California Invasion of Privacy Act (CIPA, Cal. Penal Code § 631) and federal wiretap statutes, CatchAI Text strictly deploys zero session replay, keystroke logging, or screen recording software (e.g., Hotjar, FullStory, Clarity, or PostHog). Keystrokes typed into writing analysis boxes reside entirely within local browser memory and are never intercepted or monitored in transit.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  4. 100% Self-Hosted Assets &amp; Zero External IP Leakage
                </h3>
                <p>
                  In compliance with European data protection jurisprudence (including Munich Regional Court ruling <em>LG München I, Az. 3 O 17493/20</em>), all web fonts (Inter, Plus Jakarta Sans), icons, and stylesheets are self-hosted locally from our origin infrastructure. No user IP addresses or browser fingerprints are transmitted to Google Fonts or external CDNs.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  5. Cookie Policy &amp; Tracking Transparency
                </h3>
                <p>
                  CatchAI Text sets zero advertising, cross-site, or behavioural tracking cookies. Local browser storage is used exclusively for functional state persistence: theme preference, active session weights, and device-bound scan history.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  6. Children&rsquo;s Online Privacy Protection (COPPA 16 CFR Part 312 / FERPA)
                </h3>
                <p>
                  CatchAI Text is intended for educational users aged 13 and older, or school-enrolled students operating under institutional educator supervision in compliance with FERPA and COPPA educational consent frameworks. We do not knowingly collect, retain, or solicit personal information from children under 13.
                </p>
              </section>
            </article>
          )}

          {/* 3. ETHICS & AI GOVERNANCE */}
          {activeSection === "ethics" && (
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              <div className="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/50 flex items-start gap-3.5 not-prose">
                <Scale className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-indigo-950 dark:text-indigo-200 text-sm">
                    EU AI Act (Annex III) &bull; High-Risk Education Safeguards
                  </h4>
                  <p className="text-xs text-indigo-900/90 dark:text-indigo-300/90 mt-0.5 leading-relaxed">
                    Under Article 6 and Annex III of Regulation (EU) 2024/1689, AI systems used in educational assessments are categorized as high-risk. CatchAI Text is built to uphold human autonomy and non-discrimination.
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  1. High-Risk Educational Assessment Governance
                </h3>
                <p>
                  The European Union AI Act classifies AI systems evaluating students in educational institutions as High-Risk. In accordance with mandatory transparency, human oversight, and accuracy requirements:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Human Autonomy:</strong> The system cannot execute automated disciplinary penalties or grade deductions.</li>
                  <li><strong>Explainability by Design:</strong> Instead of producing a single opaque verdict, CatchAI Text exposes sentence-level attributions and individual stylometric feature values.</li>
                  <li><strong>Traceability:</strong> Full transparency into the underlying statistical distributions, logistic regression weights, and confidence intervals.</li>
                </ul>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  2. Non-Native English (ESL / EFL) Writer Equity
                </h3>
                <p>
                  Empirical studies (e.g. Stanford University, Liang et al.) demonstrate that non-native English speakers are disproportionately misclassified by naive AI detectors due to their more standardized vocabulary and conservative syntax.
                </p>
                <p>
                  CatchAI Text incorporates dedicated calibration offsets for international writing and instructs educators to never penalize non-native writing without reviewing personal drafting conferences and outline notes.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  3. Environmental &amp; Computational Responsibility
                </h3>
                <p>
                  Instead of dispatching massive server-side GPU clusters for simple linguistic checks, CatchAI Text executes fast mathematical kernels directly on the user's browser CPU. This slashes carbon footprint and eliminates cloud computing waste.
                </p>
              </section>
            </article>
          )}

          {/* 4. ABOUT CATCHAI TEXT & METHODOLOGY */}
          {activeSection === "about" && (
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              <div className="p-4 rounded-2xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-start gap-3.5 not-prose">
                <Building className="w-5 h-5 text-slate-700 dark:text-slate-300 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white text-sm">
                    CatchAI Text &bull; Forensic Stylometric Architecture
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-relaxed">
                    Designed and developed by Awana Jonathan &amp; Ogaga-Ochuko D. Oghoghome &mdash; Department of Computer Engineering Technology, Petroleum Training Institute (PTI), Effurun, Delta State, Nigeria.
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  1. Project Mission &amp; Institutional Provenance
                </h3>
                <p>
                  CatchAI Text was engineered to address the urgent global need for transparent, explainable, and ethically grounded writing forensics in higher education, secondary instruction, academic publishing, and digital journalism. Where commercial black-box detectors require educators to upload confidential student manuscripts to third-party cloud servers and accept an unexplained percentage score, CatchAI Text executes open mathematical stylometry directly inside the client browser.
                </p>
                <p>
                  Developed by computer engineering researchers <strong>Awana Jonathan</strong> and <strong>Ogaga-Ochuko D. Oghoghome</strong> at the Petroleum Training Institute (PTI), the platform bridges statistical natural language processing with strict student privacy preservation (FERPA, COPPA, and GDPR compliance).
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  2. Scientific Methodology &amp; 10-Dimensional Stylometric Kernel
                </h3>
                <p>
                  Rather than relying on brittle vocabulary blacklists, CatchAI Text evaluates ten orthogonal statistical dimensions of prose structure across a pre-calibrated 14,015-sample benchmark corpus:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose pt-2">
                  <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850/50 space-y-1">
                    <h5 className="font-bold text-xs text-slate-900 dark:text-white">Syntactic Burstiness (CV = σ / μ)</h5>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Evaluates standard deviation and coefficient of variation of sentence lengths across paragraphs.</p>
                  </div>
                  <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850/50 space-y-1">
                    <h5 className="font-bold text-xs text-slate-900 dark:text-white">Shannon Unigram Entropy</h5>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Measures lexical surprise and token-level information bits across vocabulary distributions.</p>
                  </div>
                  <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850/50 space-y-1">
                    <h5 className="font-bold text-xs text-slate-900 dark:text-white">Zipf&apos;s Law Power-Law Fit (R²)</h5>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Tests log-log rank-frequency regression against natural human power-law decay curves.</p>
                  </div>
                  <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850/50 space-y-1">
                    <h5 className="font-bold text-xs text-slate-900 dark:text-white">Herdan&apos;s C Index (log V / log N)</h5>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Calculates length-invariant logarithmic type-token ratios across short and long essays.</p>
                  </div>
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  3. Ongoing Curation &amp; Structural Maintenance Changelog
                </h3>
                <p>
                  CatchAI Text undergoes continuous empirical curation, false-positive auditing, and architectural maintenance to remain accurate against evolving autoregressive models while safeguarding authentic student writing:
                </p>
                <div className="not-prose overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 text-slate-700 dark:text-slate-300 font-semibold">
                        <th className="py-2.5 px-3.5 font-mono">Release</th>
                        <th className="py-2.5 px-3.5 font-mono">Cycle</th>
                        <th className="py-2.5 px-3.5">Curated Corpus &amp; Engineering Updates</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800 text-slate-600 dark:text-slate-400">
                      <tr>
                        <td className="py-2.5 px-3.5 font-mono font-semibold text-slate-900 dark:text-white">v3.2</td>
                        <td className="py-2.5 px-3.5 font-mono tabular-nums">Oct 2026</td>
                        <td className="py-2.5 px-3.5">Added interactive comparative writing case studies, expanded 10-dimension mathematical reference tables, and enhanced ESL/L2 false-positive equity calibration offsets.</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3.5 font-mono font-semibold text-slate-900 dark:text-white">v3.0</td>
                        <td className="py-2.5 px-3.5 font-mono tabular-nums">Sep 2026</td>
                        <td className="py-2.5 px-3.5">Integrated 14,015-row benchmark training dataset with client-side IndexedDB persistence, domain-specific weight presets (Academic, STEM Lab, Creative, Business), and printable PDF forensic certificates.</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3.5 font-mono font-semibold text-slate-900 dark:text-white">v2.4</td>
                        <td className="py-2.5 px-3.5 font-mono tabular-nums">Jul 2026</td>
                        <td className="py-2.5 px-3.5">Launched multimodal OCR Document Studio with adaptive Otsu binarization, perspective deskewing, and local WebAssembly Tesseract transcription for handwritten notes.</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3.5 font-mono font-semibold text-slate-900 dark:text-white">v1.8</td>
                        <td className="py-2.5 px-3.5 font-mono tabular-nums">Apr 2026</td>
                        <td className="py-2.5 px-3.5">Introduced sentence-level Explainable AI (XAI) heatmaps, Zipfian R² regression diagnostics, and zero-retention client-side DOCX/PDF document ingestion.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="space-y-3 pt-2">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  4. Support &amp; Technical Inquiries
                </h3>
                <p>
                  For user support, pedagogical guidance, curriculum rubrics, or general feedback:
                </p>
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 flex items-center justify-between gap-4 not-prose flex-wrap">
                  <div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white">CatchAI Text Support Desk</div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">Official email for assistance, institutional questions, and feedback.</div>
                  </div>
                  <a
                    href="mailto:catchaitext@gmail.com"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors shadow-xs"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span>catchaitext@gmail.com</span>
                  </a>
                </div>
              </section>
            </article>
          )}

          {/* 5. DMCA & COPYRIGHT */}
          {activeSection === "dmca" && (
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              <div className="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/50 flex items-start gap-3.5 not-prose">
                <Copyright className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-indigo-950 dark:text-indigo-200 text-sm">
                    DMCA Section 512 Safe Harbor &bull; Copyright Compliance Policy
                  </h4>
                  <p className="text-xs text-indigo-900/90 dark:text-indigo-300/90 mt-0.5 leading-relaxed">
                    CatchAI Text complies fully with 17 U.S.C. § 512. Uploaded documents and OCR scans are processed in-memory without public hosting or publication.
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  1. Ephemeral Processing &amp; Zero Public Distribution
                </h3>
                <p>
                  CatchAI Text provides diagnostic stylometric analysis and optical character recognition (OCR). Uploaded documents, images, and camera scans are processed strictly in-memory or on-device via WebAssembly. CatchAI Text operates no public repositories, indexing engines, or content-sharing feeds. Uploaded works are never published or distributed to third parties.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  2. User Representations &amp; Educational Fair Use
                </h3>
                <p>
                  By uploading or pasting text, documents, or images into CatchAI Text, you affirm that: (a) you are the author or copyright owner of the submitted content, (b) you possess express authorization from the rights holder, or (c) your submission qualifies as fair use under 17 U.S.C. § 107 for the sole non-commercial purpose of scholarship, education, research, or authorship authenticity verification.
                </p>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  3. Designated DMCA Copyright Agent
                </h3>
                <p>
                  If you believe that content processed or referenced on CatchAI Text infringes your copyright, you may submit a formal notification pursuant to 17 U.S.C. § 512(c)(3) to our designated compliance office:
                </p>
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 space-y-1.5 font-mono text-xs text-slate-700 dark:text-slate-300 not-prose">
                  <p><strong>Designated Agent:</strong> CatchAI Text Copyright &amp; Academic Integrity Compliance Office</p>
                  <p><strong>Domain:</strong> catchaitext.com</p>
                  <p><strong>Email:</strong> catchaitext@gmail.com</p>
                  <p><strong>Statutory Safe Harbor:</strong> 17 U.S.C. § 512(c) / Registered Agent</p>
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  4. Required Elements of a Formal Notice
                </h3>
                <p>
                  Under 17 U.S.C. § 512(c)(3), a valid notification of claimed infringement must include:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>A physical or electronic signature of the copyright owner or authorized representative.</li>
                  <li>Identification of the copyrighted work claimed to have been infringed.</li>
                  <li>Identification of the specific material claimed to be infringing.</li>
                  <li>Contact information (postal address, telephone number, and email address).</li>
                  <li>A statement that you have a good faith belief that the use is not authorized by the copyright owner, its agent, or the law.</li>
                  <li>A statement under penalty of perjury that the notification is accurate and you are authorized to act.</li>
                </ul>
              </section>
            </article>
          )}

          {/* 6. HOW TO INTERPRET SCORES GUIDE */}
          {activeSection === "score-guide" && (
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              <div className="p-4 rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/50 flex items-start gap-3.5 not-prose">
                <HelpCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-indigo-950 dark:text-indigo-200 text-sm">
                    Probabilistic Confidence Bands &bull; Human-in-the-Loop Evaluation Rubric
                  </h4>
                  <p className="text-xs text-indigo-900/90 dark:text-indigo-300/90 mt-0.5 leading-relaxed">
                    CatchAI Text reports statistical probabilities and sentence-level syntactical surprisal. A score is an analytical indicator to guide conversation, never conclusive proof of misconduct.
                  </p>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  1. The Four Confidence Tiers
                </h3>
                <p>
                  Every analyzed document produces an overarching probabilistic score alongside sentence-level attributions. Interpret scores according to these standardized academic tiers:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose pt-2">
                  {FORENSIC_SCORE_TIERS.map((tier) => (
                    <div
                      key={tier.id}
                      className={`p-4 rounded-xl border ${tier.colorScheme.border} ${tier.colorScheme.bg} space-y-1.5`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-bold text-xs ${tier.colorScheme.titleColor}`}>{tier.range}</span>
                        <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${tier.colorScheme.badgeBg} ${tier.colorScheme.badgeText} font-bold`}>
                          {tier.badge}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                        {tier.description}
                      </p>
                      <div className="pt-1 text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                        <strong>Action:</strong> {tier.recommendedAction}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  2. Core Mathematical Stylometric Dimensions
                </h3>
                <p>
                  CatchAI Text measures multiple orthogonal dimensions rather than superficial keyword matching:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong>Syntactic Burstiness (Sentence Length CV):</strong> Human writers fluctuate wildly between 4-word punches and 35-word complex compound sentences. Language models generate homogenous cadences.</li>
                  <li><strong>Perplexity &amp; Surprisal:</strong> Evaluates how unexpected consecutive words are. AI minimizes perplexity to produce fluent, generic phrasing.</li>
                  <li><strong>Shannon Entropy:</strong> Measures unigram and bigram information density. Machine text exhibits compressed, low-entropy distributions.</li>
                  <li><strong>Zipf&apos;s Law R² Fit:</strong> Natural human language follows George Kingsley Zipf&apos;s inverse power law. AI translations and paraphrasers frequently distort this decay curve.</li>
                  <li><strong>Herdan&apos;s C Index:</strong> A length-invariant logarithmic measure of lexical variety (vocabulary breadth).</li>
                </ul>
              </section>

              <section className="space-y-3">
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">
                  3. Ethical Protocols for Educators &amp; Evaluators
                </h3>
                <p>
                  In accordance with institutional guidelines and the EU AI Act:
                </p>
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 space-y-2 not-prose text-xs text-slate-700 dark:text-slate-300">
                  <p><strong>1. Never impose punitive sanctions on a score alone.</strong> Statistical models cannot attest to intent, memory, or draft history.</p>
                  <p><strong>2. Verify drafting provenance:</strong> Ask students for their Google Docs edit histories, timestamped commits, or preliminary outlines.</p>
                  <p><strong>3. Conduct a formative student conference:</strong> Ask the author to orally explain their thesis, literature citations, and drafting evolution.</p>
                  <p><strong>4. Consider ESL / EFL background:</strong> International and non-native writers produce more conservative syntax that naive models over-flag.</p>
                </div>
              </section>
            </article>
          )}

        </main>
      </div>

      {/* Standard Footer for Legal Center */}
      <footer className="border-t border-slate-200 dark:border-slate-850 py-8 bg-white dark:bg-slate-950 mt-12 text-xs text-slate-500 dark:text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>
            &copy; {new Date().getFullYear()} CatchAI Text. All rights reserved.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                onNavigateHome();
              }}
              className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
            >
              Document Detector
            </a>
            <span>&bull;</span>
            <a
              href="/about"
              onClick={(e) => {
                e.preventDefault();
                handleSelectSection("about");
              }}
              className="hover:underline cursor-pointer"
            >
              About &amp; Methodology
            </a>
            <span>&bull;</span>
            <a
              href="/score-guide"
              onClick={(e) => {
                e.preventDefault();
                handleSelectSection("score-guide");
              }}
              className="hover:underline cursor-pointer"
            >
              Score Guide
            </a>
            <span>&bull;</span>
            <a
              href="/privacy"
              onClick={(e) => {
                e.preventDefault();
                handleSelectSection("privacy");
              }}
              className="hover:underline cursor-pointer"
            >
              Privacy Policy
            </a>
            <span>&bull;</span>
            <a
              href="/terms"
              onClick={(e) => {
                e.preventDefault();
                handleSelectSection("terms");
              }}
              className="hover:underline cursor-pointer"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
};
